import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { SurveyStatus, SurveyType } from "@/shared/utils/constants";

import { useFilterCount } from "@/shared/hooks/useFilterCount";

import "../styles.css";
import {
  SurveyFiltersProps,
  SurveyFiltersState,
} from "@/features/survey/list/types";

const surveyFiltersSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      type: yup.string().typeError(t("form.validation.notValid")),
      status: yup.string().typeError(t("form.validation.notValid")),
    })
    .required();

const SurveyFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: SurveyFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<SurveyFiltersState>({
    // @ts-expect-error filters state is same as schema
    resolver: yupResolver(surveyFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: "",
      type: "",
      status: "",
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "title",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="title"
            control={form.control}
            className="survey-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("survey.placeholders.searchSurveys")}
          />
        ),
      },
      type: {
        name: "type",
        label: t("survey.labels.type"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="type"
            control={form.control}
            options={SurveyType.asList()}
            placeholder={t("survey.placeholders.selectSurveyType")}
            allowClear={true}
          />
        ),
      },
      status: {
        name: "status",
        label: t("survey.labels.status"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="status"
            control={form.control}
            options={SurveyStatus.asList()}
            placeholder={t("survey.placeholders.selectSurveyStatus")}
            allowClear={true}
          />
        ),
      },
    };
  }, []);

  const activeFilterCount = useFilterCount<SurveyFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("survey.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="survey-filters"
    />
  );
};

export default SurveyFilters;
